import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

export type SelectOption = {
  value: string;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** Subtle group header rendered above the first option in each group.
   *  Options sharing the same `group` value (in source order) sit under one header.
   *  Options without `group` are emitted ungrouped. */
  group?: string;
};

type Props = {
  value: string | number | null | undefined;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  /** Shows a small caret-only trigger instead of full-width (for inline usages like the tenant switcher). */
  compact?: boolean;
};

// Themed, searchable replacement for the native <select>. Always renders a
// search input at the top of the dropdown — per chambers-wide UI rule.
export default function Select({
  value, onChange, options,
  placeholder = '— pick one —',
  searchPlaceholder = 'Search…',
  disabled, required, style, compact,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value ?? '')),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      o.label.toLowerCase().includes(q) ||
      String(o.value).toLowerCase().includes(q) ||
      (o.hint || '').toLowerCase().includes(q),
    );
  }, [options, query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the search input when opened; reset filter.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open]);

  // Keep active option visible.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const commit = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(filtered.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIdx];
      if (opt && !opt.disabled) commit(String(opt.value));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', ...style }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          padding: compact ? '6px 10px' : '8px 12px',
          paddingRight: 32,
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-required={required}
      >
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: selected ? 'var(--color-text)' : 'var(--color-text-muted)',
        }}>
          {selected ? selected.label : placeholder}
        </span>
        <Icon name="chevron-down" size={14} style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--color-text-muted)',
        }} />
      </button>

      {open && (
        <div style={popoverStyle} role="listbox">
          <div style={searchWrap}>
            <Icon name="search" size={14} style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)', zIndex: 1 }} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
              onKeyDown={onKey}
              placeholder={searchPlaceholder}
              style={{ padding: '6px 10px 6px 32px', width: '100%', fontSize: 13, height: 32, boxSizing: 'border-box' }}
            />
          </div>
          <div ref={listRef} style={listStyle}>
            {filtered.length === 0 && (
              <div className="muted" style={{ padding: '10px 12px', fontSize: 13 }}>No matches.</div>
            )}
            {filtered.map((o, i) => {
              const isSelected = String(o.value) === String(value ?? '');
              const isActive = i === activeIdx;
              // Emit a subtle header row when this option's group differs
              // from the previous one. Headers are non-interactive and don't
              // affect keyboard nav (which iterates `filtered` indices).
              const prev = i > 0 ? filtered[i - 1] : null;
              const showHeader = !!o.group && o.group !== prev?.group;
              return (
                <div key={String(o.value)}>
                  {showHeader && (
                    <div
                      style={{
                        padding: i === 0 ? '6px 12px 4px' : '10px 12px 4px',
                        fontSize: 10,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: 'var(--color-text-muted)',
                        background: 'var(--color-surface)',
                        borderTop: i === 0 ? 'none' : '1px solid var(--color-border)',
                        marginTop: i === 0 ? 0 : 4,
                      }}
                      aria-hidden="true"
                    >
                      {o.group}
                    </div>
                  )}
                  <button
                    type="button"
                    tabIndex={-1}
                    data-idx={i}
                    role="option"
                    aria-selected={isSelected}
                    disabled={o.disabled}
                    onMouseEnter={() => setActiveIdx(i)}
                    // preventDefault here keeps focus on the search input (no
                    // blur-triggered re-render) while still letting the click
                    // fire on this button.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!o.disabled) commit(String(o.value));
                    }}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      cursor: o.disabled ? 'not-allowed' : 'pointer',
                      background: isActive ? 'var(--color-surface-2)' : 'transparent',
                      color: o.disabled ? 'var(--color-text-muted)' : 'var(--color-text)',
                      fontSize: 13,
                      display: 'flex', alignItems: 'center', gap: 8,
                      borderLeft: isSelected ? '2px solid var(--color-accent)' : '2px solid transparent',
                      borderTop: 'none', borderRight: 'none', borderBottom: 'none',
                      borderRadius: 0,
                      textAlign: 'left',
                      fontWeight: 400,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.label}
                      {o.hint && <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>{o.hint}</span>}
                    </span>
                    {isSelected && <span style={{ color: 'var(--color-accent)', fontWeight: 700 }}>✓</span>}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const triggerStyle: React.CSSProperties = {
  width: '100%',
  position: 'relative',
  display: 'flex', alignItems: 'center',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  fontSize: 14,
  textAlign: 'left',
  fontWeight: 400,
};

const popoverStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
  zIndex: 50,
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  boxShadow: 'var(--shadow)',
  overflow: 'hidden',
  minWidth: 220,
};

const searchWrap: React.CSSProperties = {
  position: 'relative',
  padding: 8,
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
};

const listStyle: React.CSSProperties = {
  maxHeight: 260, overflowY: 'auto',
  padding: '4px 0',
};
